import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, query, orderBy, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { useLocation, Link } from 'react-router-dom';
import ImageModal from '../components/ImageModal'; 
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { ToastContainer, toast } from 'react-toastify';
import { FaTrash, FaShoppingCart, FaPlus, FaSearch } from 'react-icons/fa';
import axios from 'axios';

const Products = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  // const shopId = queryParams.get('shop');

  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Search and load more state
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleDesigns, setVisibleDesigns] = useState(7);
  const designsPerLoad = 7;
  
  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [designToDelete, setDesignToDelete] = useState(null);
  const [showPushModal, setShowPushModal] = useState(false);
  const [designToPush, setDesignToPush] = useState(null);
  
  // Push to store state
  const [pushLoading, setPushLoading] = useState(false);
  const [pushMessage, setPushMessage] = useState('');
  const [pushStatus, setPushStatus] = useState(''); // 'success' or 'error'

  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not logged in");
  }

  useEffect(() => {
    const fetchDesigns = async () => {
      try {
        const designsCollection = collection(db, 'users', user.uid, 'designs');
        const q = query(designsCollection, orderBy('timestamp', 'desc'));
        const designSnapshot = await getDocs(q);
        const designList = designSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDesigns(designList);
      } catch (error) {
        console.error("Error fetching designs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDesigns();
  }, []);

  // Helper function to determine product type
  const getProductType = (design) => {
    return design.productType || 'tshirt'; // Default to tshirt if not specified
  };

  // Helper function to get print costs based on product type
  const getPrintCosts = (design) => {
    const productType = getProductType(design);
    
    if (productType === 'joggers') {
      return {
        front: design.PrintCost474 || 0,
        back: design.PrintCost474 || 0,
        total: design.PrintCost474 || 0
      };
    } else {
      // T-shirt format
      return {
        front: design.costs?.front || 0,
        back: design.costs?.back || 0,
        total: (design.costs?.front || 0) + (design.costs?.back || 0)
      };
    }
  };

  // Helper function to get base cost
  const getBaseCost = (design) => {
    const productType = getProductType(design);
    
    if (productType === 'joggers') {
      return design.baseCost570 || 0;
    } else {
      return design.baseCost || 0;
    }
  };

  // Helper function to get product specifications
  const getProductSpecs = (design) => {
    const productType = getProductType(design);
    
    if (productType === 'joggers') {
      return design.productGSM || 'N/A';
    } else {
      return design.productSpecs || 'N/A';
    }
  };

  // Filter designs based on search term
  const filteredDesigns = designs.filter(design => 
    design.designName.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Get the currently visible designs
  const currentDesigns = filteredDesigns.slice(0, visibleDesigns);

  // Handle load more button click
  const handleLoadMore = () => {
    setVisibleDesigns(prev => prev + designsPerLoad);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setVisibleDesigns(designsPerLoad); // Reset to initial load amount when searching
  };

  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc); // Set the clicked image as the selected one
  };

  const handleCloseModal = () => {
    setSelectedImage(null); // Close the modal by clearing the selected image
  };

  const handleDeleteClick = (designId) => {
    setDesignToDelete(designId); // Set the design ID to be deleted
    setShowDeleteModal(true); // Show the confirmation modal
  };

  const handleDelete = async () => {
    try {
      const designDoc = doc(db, 'users', user.uid, 'designs', designToDelete);
      await deleteDoc(designDoc);
      setDesigns(designs.filter(design => design.id !== designToDelete)); // Remove deleted design from the UI
    } catch (error) {
      console.error("Error deleting design:", error);
    } finally {
      setShowDeleteModal(false); // Close the modal after deletion
      setDesignToDelete(null); // Clear the design ID
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false); // Close the modal if the user cancels
    setDesignToDelete(null); // Clear the design ID
  };

  // Push to Store functionality
  const handlePushToStoreClick = (design) => {
    setDesignToPush(design);
    setShowPushModal(true);
    // Reset status messages
    setPushMessage('');
    setPushStatus('');
  };

  const handleCancelPush = () => {
    setShowPushModal(false);
    setDesignToPush(null);
  };

  const handlePushToStore = async () => {
    setPushLoading(true);
    setPushMessage('Pushing product to Shopify store...');
    
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
  
      if (!userDoc.exists()) {
        return;
      }
  
      const userData = userDoc.data();
      const userShop = userData.shop;

      const payload = {
        productName: designToPush.productName,
        frontImage: designToPush.images.front,
        backImage: designToPush.images.back,
        shop: userShop,
        designName: designToPush.designName,
        description: designToPush.description,
        sellingPrice: designToPush.sellingPrice,
        SKUs: designToPush.SKUs,
        status: 'ACTIVE', // Default to active
        userId: user.uid,
        productType: getProductType(designToPush) // Include product type
      };

      const response = await axios.post('https://ufb-1.onrender.com/push-to-store', payload);
      
      setPushStatus('success');
      setPushMessage(`Product successfully pushed to Shopify! Product ID: ${response.data.productId}`);
      toast.success("Product is live on Shopify!");
    } catch (error) {
      console.error('Error pushing to store:', error);
      setPushStatus('error');
      setPushMessage(`Error pushing to store: ${error.response?.data?.message || error.message}`);
    } finally {
      setPushLoading(false);
      setShowPushModal(false);
    }
  };

  // Helper function to format size objects
  const formatSize = (sizeObj) => {
    if (!sizeObj || typeof sizeObj !== 'object') return 'N/A';
    const width = Math.floor(sizeObj.width) || 0;
    const height = Math.floor(sizeObj.height) || 0;
    return `${width}×${height}`;
  };

  const getPrintSizes = (design) => {
    const productType = getProductType(design);
    const printSizes = design.Printsizes || {};
    
    if (productType === 'joggers') {
      // For joggers, print sizes are stored in a detailed map with individual print areas
      const frontSizes = printSizes.front || {};
      const backSizes = printSizes.back || {};
      
      return {
        type: 'joggers',
        front: {
          frontLeftFull: frontSizes.frontLeftFull || { height: 0, width: 0 },
          frontLeftLogo: frontSizes.frontLeftLogo || { height: 0, width: 0 },
          frontRightFull: frontSizes.frontRightFull || { height: 0, width: 0 },
          frontRightLogo: frontSizes.frontRightLogo || { height: 0, width: 0 }
        },
        back: {
          backBumArea: backSizes.backBumArea || { height: 0, width: 0 },
          backLeftFull: backSizes.backLeftFull || { height: 0, width: 0 },
          backRightFull: backSizes.backRightFull || { height: 0, width: 0 }
        }
      };
    } else {
      // Regular t-shirt format
      return {
        type: 'regular',
        front: printSizes.front || { height: 0, width: 0 },
        back: printSizes.back || { height: 0, width: 0 }
      };
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-white">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden md:ml-64">
          <Header />
          <main className="flex-1 flex justify-center items-center">
            <div className="text-gray-600 text-lg">Loading products...</div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-white">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden md:ml-64">
        <Header />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="text-left text-3xl font-bold text-gray-800 order-management-title mb-2 uppercase">
              Products
            </h1>
          </div>

          {/* Actions Section */}
          <div className="mb-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <Link 
              to={'/selecttshirt'} 
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <FaPlus className="w-4 h-4" />
              Create Product
            </Link>
            
            <div className="relative w-full sm:w-80">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by design name..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm"
              />
            </div>
          </div>
          
          {/* Content Section */}
          {designs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Products Yet</h3>
              <p className="text-gray-600 text-center mb-6">Start by creating your first product design</p>
              <Link 
                to={'/selecttshirt'} 
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center gap-2"
              >
                <FaPlus className="w-4 h-4" />
                Create Your First Product
              </Link>
            </div>
          ) : filteredDesigns.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl">
              <div className="text-gray-400 mb-4">
                <FaSearch className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Found</h3>
              <p className="text-gray-600 text-center">Try adjusting your search terms</p>
            </div>
          ) : (
            <>
              {/* Products Table */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-max">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-32">Design</th>
                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Type</th>
                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">Method</th>
                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-16">Front</th>
                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-16">Back</th>
                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">Costs</th>
                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Sizes</th>
                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">Prices</th>
                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-16">GSM</th>
                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {currentDesigns.map(design => {
                        const productType = getProductType(design);
                        const printCosts = getPrintCosts(design);
                        const printSizes = getPrintSizes(design);
                        const baseCost = getBaseCost(design);
                        const productSpecs = getProductSpecs(design);
                        
                        return (
                          <tr key={design.id} className="hover:bg-gray-50 transition-colors duration-150">
                            <td className="px-4 py-4">
                              <div className="text-sm font-medium text-gray-900 truncate" title={design.designName}>
                                {design.designName}
                              </div>
                              <div className="text-xs text-gray-500 truncate mt-1" title={design.productName}>
                                {design.productName}
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                productType === 'joggers' 
                                  ? 'bg-purple-100 text-purple-800' 
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {productType.charAt(0).toUpperCase() + productType.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <div className="text-xs text-gray-900 space-y-1">
                                {typeof design.printMethod === 'object' && design.printMethod !== null
                                  ? Object.entries(design.printMethod)
                                      .filter(([key, value]) => value !== null && value !== undefined)
                                      .map(([key, value], index) => (
                                        <div key={index} className="truncate" title={`${key}: ${value}`}>
                                          {key}: {value}
                                        </div>
                                      ))
                                  : <div className="truncate" title={design.printMethod}>{design.printMethod}</div>
                                }
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <img 
                                src={design.images.front} 
                                alt='Front Design' 
                                className="w-12 h-12 object-cover rounded-lg cursor-pointer hover:scale-110 transition-transform duration-200 border border-gray-200" 
                                onClick={() => handleImageClick(design.images.front)}
                              />
                            </td>
                            <td className="px-4 py-4">
                              <img 
                                src={design.images.back} 
                                alt="Back Design" 
                                className="w-12 h-12 object-cover rounded-lg cursor-pointer hover:scale-110 transition-transform duration-200 border border-gray-200" 
                                onClick={() => handleImageClick(design.images.back)}
                              />
                            </td>
                            <td className="px-4 py-4">
                              <div className="text-xs text-gray-900 space-y-1">
                                <div>Print: ₹{design.PrintCost}</div>
                                <div>Base: ₹{design.baseCost}</div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              {printSizes.type === 'joggers' ? (
                                <div className="text-xs text-gray-900 space-y-1">
                                  <div className="font-medium text-gray-700">Front:</div>
                                  <div className="space-y-0.5">
                                    <div>L: {formatSize(printSizes.front.frontLeftFull)}</div>
                                    <div>R: {formatSize(printSizes.front.frontRightFull)}</div>
                                  </div>
                                  <div className="font-medium text-gray-700 mt-2">Back:</div>
                                  <div className="space-y-0.5">
                                    <div>Bum: {formatSize(printSizes.back.backBumArea)}</div>
                                    <div>L: {formatSize(printSizes.back.backLeftFull)}</div>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-xs text-gray-900 space-y-1">
                                  <div>F: {formatSize(printSizes.front)}</div>
                                  <div>B: {formatSize(printSizes.back)}</div>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              <div className="text-xs text-gray-900 space-y-1">
                                <div>Sell: ₹{design.sellingPrice}</div>
                                <div className="font-medium">Total: ₹{design.totalPrice}</div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="text-xs text-gray-900">
                                {design.productGSM}
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex flex-col gap-2">
                                <button 
                                  onClick={() => handlePushToStoreClick(design)} 
                                  className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-sm hover:shadow-md flex items-center justify-center"
                                  title="Push to Shopify Store"
                                >
                                  <FaShoppingCart className="w-4 h-4" />
                                </button>
                                <button 
                                  onClick={() => handleDeleteClick(design.id)} 
                                  className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-sm hover:shadow-md flex items-center justify-center"
                                  title="Delete Product"
                                >
                                  <FaTrash className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Load More Button */}
              {visibleDesigns < filteredDesigns.length && (
                <div className="mt-8 flex justify-center">
                  <button 
                    onClick={handleLoadMore} 
                    className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-3 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
                  >
                    Load More Products
                  </button>
                </div>
              )}
              
              <ImageModal imageSrc={selectedImage} onClose={handleCloseModal} />
            </>
          )}
        </main>
      </div>

      {/* Confirmation Modal for Deleting */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 animate-fadeIn">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Product</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to delete this design? This action cannot be undone.</p>
              
              <div className="flex gap-3 justify-center">
                <button 
                  onClick={handleCancelDelete} 
                  className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-medium transition-all duration-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleDelete} 
                  className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-all duration-200 transform hover:scale-105"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Push to Store Modal */}
      {showPushModal && designToPush && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4 animate-fadeIn">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <FaShoppingCart className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Push Product to Shopify Store</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to push "{designToPush.designName}" to your Shopify store?</p>
              
              {pushMessage && (
                <div className={`p-4 rounded-lg mb-6 ${
                  pushStatus === 'success' 
                    ? 'bg-green-50 border border-green-200 text-green-800' 
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  <div className="flex items-center">
                    {pushStatus === 'success' ? (
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                      </svg>
                    )}
                    <span className="text-sm font-medium">{pushMessage}</span>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 justify-center">
                <button 
                  onClick={handleCancelPush} 
                  className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-medium transition-all duration-200"
                  disabled={pushLoading}
                >
                  {pushStatus === 'success' ? 'Close' : 'Cancel'}
                </button>
                <button 
                  onClick={handlePushToStore} 
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg font-medium transition-all duration-200 transform hover:scale-105 disabled:transform-none"
                  disabled={pushLoading || pushStatus === 'success'}
                >
                  {pushLoading ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Pushing...
                    </div>
                  ) : (
                    'Yes, Push to Store'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;